import React from 'react';
import { motion } from 'framer-motion';
import { Hexagon, ShieldCheck, Coins, Settings, Gift, Swords, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export function ExtensionFrame({ children, currentPage, onNavigate }) {
  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden md:flex min-h-screen bg-[#09090B] w-full">
        <DesktopSidebar currentPage={currentPage} onNavigate={onNavigate} />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-8 py-8">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden min-h-screen bg-[#09090B] flex items-center justify-center p-4 honeycomb-bg">
        <div className="w-full max-w-[420px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative bg-[#09090B] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            style={{ minHeight: '620px' }}
          >
            <div className="bg-[#121214] border-b border-white/5 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              </div>
              <div className="flex-1 flex items-center justify-center gap-2">
                <Hexagon className="w-4 h-4 text-amber-500 fill-amber-500/20" />
                <span className="text-xs font-heading font-bold text-amber-500 tracking-wide">HONEY POT</span>
              </div>
              <div className="w-12" />
            </div>
            <div className="flex flex-col" style={{ minHeight: '570px' }}>
              <div className="flex-1 overflow-y-auto">
                {children}
              </div>
              {currentPage && (
                <MobileNav currentPage={currentPage} onNavigate={onNavigate} />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

function DesktopSidebar({ currentPage, onNavigate }) {
  const { user } = useAuth();

  const navItems = [
    { id: 'home', icon: ShieldCheck, label: 'Protect' },
    { id: 'quests', icon: Swords, label: 'Quests' },
    { id: 'rewards', icon: Gift, label: 'Rewards' },
    { id: 'points', icon: Coins, label: 'Points' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <aside className="w-56 flex-shrink-0 border-r border-white/5 bg-[#0C0C0E] flex flex-col">
      {/* Logo */}
      <div className="h-16 border-b border-white/5 flex items-center px-5 gap-3">
        <Hexagon className="w-7 h-7 text-amber-500 fill-amber-500/20" />
        <span className="font-heading font-black text-base">
          <span className="text-white">Honey</span>
          <span className="text-amber-500">Pot</span>
        </span>
      </div>

      {/* User */}
      <div className="px-4 py-4 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 hex-clip bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-black text-black">{user?.level || 1}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">{user?.name}</p>
            <p className="text-[10px] text-zinc-500">Level {user?.level || 1} Protector</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-1">
        {navItems.map(item => {
          const active = currentPage === item.id;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                active
                  ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03] border border-transparent'
              }`}
            >
              <item.icon className={`w-4.5 h-4.5 ${active ? 'text-amber-500' : ''}`} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Points footer */}
      <div className="border-t border-white/5 px-4 py-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-500">Points</span>
          <span className="font-mono font-bold text-amber-500">{user?.points || 0}</span>
        </div>
      </div>
    </aside>
  );
}

function MobileNav({ currentPage, onNavigate }) {
  const navItems = [
    { id: 'home', icon: ShieldCheck, label: 'Protect' },
    { id: 'quests', icon: Swords, label: 'Quests' },
    { id: 'rewards', icon: Gift, label: 'Rewards' },
    { id: 'points', icon: Coins, label: 'Points' },
    { id: 'settings', icon: Settings, label: 'More' },
  ];

  return (
    <div className="border-t border-white/5 bg-[#0C0C0E] px-2 py-1.5">
      <div className="flex items-center justify-around">
        {navItems.map(item => (
          <button
            key={item.id}
            data-testid={`nav-${item.id}`}
            onClick={() => onNavigate(item.id)}
            className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all duration-200 ${
              currentPage === item.id
                ? 'text-amber-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            <item.icon className={`w-5 h-5 ${currentPage === item.id ? 'drop-shadow-[0_0_6px_rgba(245,158,11,0.5)]' : ''}`} />
            <span className="text-[10px] font-medium">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
